/**
 * Состояние карты в адресной строке.
 *
 * Ссылка на конкретный участок генплана — основной способ, которым люди
 * передают друг другу «вот про это место я говорю». Поэтому в хеш попадает не
 * только положение камеры, но и набор включённых слоёв, тема подложки и
 * положение шторки сравнения: получатель ссылки должен увидеть ровно то же
 * самое, а не «тот же город, но другую карту».
 *
 * Формат: #z/lat/lng/bearing/pitch?layers=a,b&theme=light&swipe=0.5&cmp=a|b&sel=src__layer__id
 */

export interface ViewState {
  zoom: number;
  lat: number;
  lng: number;
  bearing: number;
  pitch: number;
}

export interface PermalinkState {
  view: ViewState;
  layers: string[];
  theme: string;
  /** Положение шторки сравнения 0..1, null — режим сравнения выключен. */
  swipe: number | null;
  /** Выбранный объект в виде "источник__слой__id", null — ничего не выбрано. */
  selection: string | null;
  /**
   * Пара слоёв для шторки в виде "слева|справа".
   * Без неё положение шторки бессмысленно: получатель ссылки увидел бы
   * шторку на том же месте, но между другими слоями.
   */
  compare: string | null;
}

const HASH_PREFIX = "#";

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

/** Точность координат подбирается под зум: на z18 нужно больше знаков, чем на z6. */
function coordinatePrecision(zoom: number): number {
  return Math.max(4, Math.min(7, Math.ceil(zoom / 3) + 2));
}

export function encodeState(state: PermalinkState): string {
  const { view } = state;
  const precision = coordinatePrecision(view.zoom);
  const parts = [
    round(view.zoom, 2),
    round(view.lat, precision),
    round(view.lng, precision),
    round(view.bearing, 1),
    round(view.pitch, 1),
  ];

  const params = new URLSearchParams();
  // layers пишется всегда, даже пустым. Иначе «все слои выключены» кодируется
  // так же, как «параметр не задан», а получатель ссылки трактует это как
  // «включить всё» и видит другую карту.
  params.set("layers", state.layers.join(","));
  // Тема пишется только при явном выборе. Иначе ссылка от человека, у которого
  // тема отдана системе, навязывала бы получателю светлую или тёмную — а тот,
  // возможно, хочет свою системную.
  if (state.theme && state.theme !== "auto") params.set("theme", state.theme);
  if (state.swipe !== null) params.set("swipe", String(round(state.swipe, 3)));
  if (state.selection !== null) params.set("sel", state.selection);
  if (state.compare !== null) params.set("cmp", state.compare);

  const query = params.toString();
  return `${HASH_PREFIX}${parts.join("/")}${query ? `?${query}` : ""}`;
}

export function decodeState(hash: string = window.location.hash): Partial<PermalinkState> {
  const raw = hash.startsWith(HASH_PREFIX) ? hash.slice(1) : hash;
  if (raw === "") return {};

  const [positionPart, queryPart] = raw.split("?", 2);
  const result: Partial<PermalinkState> = {};

  const numbers = (positionPart ?? "").split("/").map((chunk) => Number.parseFloat(chunk));
  const [zoom, lat, lng, bearing, pitch] = numbers;
  if (
    Number.isFinite(zoom) &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat as number) <= 90 &&
    Math.abs(lng as number) <= 180
  ) {
    result.view = {
      zoom: zoom as number,
      lat: lat as number,
      lng: lng as number,
      bearing: Number.isFinite(bearing) ? (bearing as number) : 0,
      pitch: Number.isFinite(pitch) ? (pitch as number) : 0,
    };
  }

  if (queryPart) {
    const params = new URLSearchParams(queryPart);
    const layers = params.get("layers");
    if (layers !== null) {
      result.layers = layers.split(",").filter((value) => value !== "");
    }
    const theme = params.get("theme");
    if (theme) result.theme = theme;
    const swipe = params.get("swipe");
    if (swipe !== null) {
      const parsed = Number.parseFloat(swipe);
      if (Number.isFinite(parsed)) result.swipe = Math.min(1, Math.max(0, parsed));
    }
    const selection = params.get("sel");
    if (selection) result.selection = selection;
    const compare = params.get("cmp");
    if (compare?.includes("|")) result.compare = compare;
  }

  return result;
}

/**
 * Пишет состояние в адресную строку через replaceState.
 *
 * pushState здесь был бы ошибкой: каждое движение карты засоряло бы историю
 * браузера, и кнопка «Назад» переставала бы работать как ожидает пользователь.
 */
export function createPermalinkWriter(delayMs = 250): (state: PermalinkState) => void {
  let timer: number | undefined;
  let pending: PermalinkState | null = null;

  const flush = (): void => {
    timer = undefined;
    if (!pending) return;
    const hash = encodeState(pending);
    pending = null;
    if (hash !== window.location.hash) {
      window.history.replaceState(null, "", hash);
    }
  };

  return (state: PermalinkState): void => {
    pending = state;
    if (timer !== undefined) return;
    timer = window.setTimeout(flush, delayMs);
  };
}
